import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Download, Eye, CreditCard } from 'lucide-react'

import { useToast } from '@hooks/use-toast'
import { usePatient } from '@hooks/usePatients'
import { billingApi } from '@services/billing.service'
import { Invoice } from '@/types'
import { InvoiceDetailDialog } from '@components/billing/InvoiceDetailDialog'
import { RecordPaymentDialog } from '@components/billing/RecordPaymentDialog'
import { formatCurrency, cn } from '@lib/utils'
import { useClinicFormat } from '@context/ClinicTimezoneContext'

const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
  draft: { bg: 'bg-ml-surface-container-high', text: 'text-ml-on-surface-variant', label: 'Borrador' },
  sent: { bg: 'bg-ml-secondary-container', text: 'text-ml-on-secondary-container', label: 'Enviada' },
  paid: { bg: 'bg-ml-secondary-container', text: 'text-[#1b3a2f]', label: 'Pagado' },
  overdue: { bg: 'bg-ml-error-container', text: 'text-ml-error', label: 'Vencida' },
  partially_paid: { bg: 'bg-ml-surface-container-high', text: 'text-ml-on-surface-variant', label: 'Parcial' },
  pending: { bg: 'bg-ml-surface-container-high', text: 'text-ml-on-surface-variant', label: 'Pendiente' },
  cancelled: { bg: 'bg-ml-surface-container-high', text: 'text-ml-on-surface-variant/60', label: 'Cancelada' },
}

export function PatientBillingHistoryPage() {
  const { patientId = '' } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { formatDate } = useClinicFormat()

  const { data: patient } = usePatient(patientId, { enabled: !!patientId })
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)

  const [detailInvoice, setDetailInvoice] = useState<Invoice | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [paymentInvoice, setPaymentInvoice] = useState<Invoice | null>(null)
  const [paymentOpen, setPaymentOpen] = useState(false)

  useEffect(() => {
    if (patientId) loadInvoices()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId])

  const loadInvoices = async () => {
    try {
      setLoading(true)
      const response = await billingApi.getInvoices({ patient_id: patientId, limit: 200 })
      setInvoices(response.items)
    } catch {
      toast({ title: 'Error', description: 'Error al cargar el historial de facturas', variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  const totalBilled = invoices.reduce((a, i) => a + Number(i.total_amount || 0), 0)
  const totalPaid = invoices.reduce((a, i) => a + Number(i.amount_paid || 0), 0)
  const outstanding = invoices.reduce((a, i) => a + Number(i.balance_due || 0), 0)
  const overdueCount = invoices.filter((i) => i.status === 'overdue').length

  const openDetail = (invoice: Invoice) => {
    setDetailInvoice(invoice)
    setDetailOpen(true)
  }

  const openPayment = (invoice: Invoice) => {
    setDetailOpen(false)
    setPaymentInvoice(invoice)
    setPaymentOpen(true)
  }

  const handleDownload = async (invoice: Invoice) => {
    try {
      const blob = await billingApi.downloadInvoice(invoice.id, 'es')
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Invoice_${invoice.invoice_number}.pdf`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (err: any) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Error al descargar factura', variant: 'destructive' })
    }
  }

  const patientName = patient ? `${patient.first_name} ${patient.last_name}` : 'Paciente'

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <button
        onClick={() => navigate('/billing')}
        className="flex items-center gap-1.5 text-xs font-bold text-ml-on-surface-variant/70 hover:text-ml-primary transition-colors mb-6 min-h-0"
      >
        <ArrowLeft className="h-4 w-4" /> Volver a Facturación
      </button>

      <div className="mb-10">
        <p className="text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/40 mb-1">Estado de Cuenta</p>
        <h2 className="font-headline text-4xl text-ml-primary tracking-tight leading-none">{patientName}</h2>
        {patient?.phone_primary && (
          <p className="text-ml-on-surface-variant/70 font-medium mt-2">{patient.phone_primary}{patient.email ? ` · ${patient.email}` : ''}</p>
        )}
      </div>

      {/* Account summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <div className="bg-ml-surface-container-lowest rounded-xl p-5 shadow-sm">
          <span className="ml-label">Total Facturado</span>
          <div className="font-headline text-2xl text-ml-primary mt-2">{formatCurrency(totalBilled)}</div>
        </div>
        <div className="bg-ml-surface-container-lowest rounded-xl p-5 shadow-sm">
          <span className="ml-label">Total Pagado</span>
          <div className="font-headline text-2xl text-ml-primary mt-2">{formatCurrency(totalPaid)}</div>
        </div>
        <div className="bg-ml-surface-container-lowest rounded-xl p-5 shadow-sm">
          <span className="ml-label">Saldo Pendiente</span>
          <div className={cn('font-headline text-2xl mt-2', outstanding > 0 ? 'text-ml-error' : 'text-ml-primary')}>
            {formatCurrency(outstanding)}
          </div>
        </div>
        <div className="bg-ml-surface-container-lowest rounded-xl p-5 shadow-sm">
          <span className="ml-label">Facturas Vencidas</span>
          <div className={cn('font-headline text-2xl mt-2', overdueCount > 0 ? 'text-ml-error' : 'text-ml-primary')}>
            {overdueCount}
          </div>
        </div>
      </div>

      {/* Invoice list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ml-primary" />
        </div>
      ) : invoices.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center bg-ml-surface-container-lowest rounded-xl">
          <h3 className="text-lg font-headline text-ml-primary mb-1">Sin Facturas</h3>
          <p className="text-ml-on-surface-variant text-sm">Este paciente todavía no tiene facturas registradas.</p>
        </div>
      ) : (
        <div className="bg-ml-surface-container-lowest rounded-xl shadow-[0_32px_32px_rgba(3,36,26,0.02)] overflow-hidden">
          <table className="w-full table-fixed">
            <thead>
              <tr className="border-b border-ml-outline-variant/10 text-[10px] uppercase tracking-widest font-extrabold text-ml-on-surface-variant/40">
                <th className="text-left px-8 py-4 w-[16%]">Ref.</th>
                <th className="text-center px-4 py-4 w-[16%]">Fecha</th>
                <th className="text-right px-4 py-4 w-[16%]">Total</th>
                <th className="text-right px-4 py-4 w-[16%]">Saldo</th>
                <th className="text-center px-4 py-4 w-[16%]">Estado</th>
                <th className="text-right px-8 py-4 w-[20%]">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => {
                const status = statusConfig[invoice.status] || statusConfig.pending
                const canPay = Number(invoice.balance_due) > 0 && invoice.status !== 'cancelled'
                return (
                  <tr
                    key={invoice.id}
                    onClick={() => openDetail(invoice)}
                    className="hover:bg-ml-surface-container-low transition-colors cursor-pointer"
                  >
                    <td className="px-8 py-5 text-xs font-mono text-ml-on-surface-variant/60 truncate" title={invoice.invoice_number}>
                      {invoice.invoice_number ? `...${invoice.invoice_number.slice(-6)}` : '#---'}
                    </td>
                    <td className="px-4 py-5 text-center text-xs text-ml-on-surface-variant font-medium">
                      {formatDate(invoice.invoice_date)}
                    </td>
                    <td className="px-4 py-5 text-right text-sm font-bold text-ml-primary">
                      {formatCurrency(invoice.total_amount)}
                    </td>
                    <td className={cn('px-4 py-5 text-right text-sm font-semibold', Number(invoice.balance_due) > 0 ? 'text-ml-error' : 'text-ml-on-surface-variant/50')}>
                      {formatCurrency(invoice.balance_due)}
                    </td>
                    <td className="px-4 py-5 text-center">
                      <span className={cn('px-3 py-1 text-[10px] font-bold rounded-sm uppercase tracking-tighter', status.bg, status.text)}>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-8 py-5 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openDetail(invoice)} title="Ver detalle" className="p-1.5 rounded-md hover:bg-ml-surface-container-high transition-colors min-h-0">
                          <Eye className="h-4 w-4 text-ml-on-surface-variant" />
                        </button>
                        {canPay && (
                          <button onClick={() => openPayment(invoice)} title="Registrar pago" className="p-1.5 rounded-md hover:bg-ml-surface-container-high transition-colors min-h-0">
                            <CreditCard className="h-4 w-4 text-ml-on-surface-variant" />
                          </button>
                        )}
                        <button onClick={() => handleDownload(invoice)} title="Descargar PDF" className="p-1.5 rounded-md hover:bg-ml-surface-container-high transition-colors min-h-0">
                          <Download className="h-4 w-4 text-ml-on-surface-variant" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <InvoiceDetailDialog
        invoice={detailInvoice}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onRecordPayment={openPayment}
        onChanged={loadInvoices}
      />
      <RecordPaymentDialog
        invoice={paymentInvoice}
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
        onSuccess={loadInvoices}
      />
    </div>
  )
}
